import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Form, Input, DatePicker, Modal, Row, Col, Spin, Tag, Typography, message,
} from 'antd';
import Moment from 'moment';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import { PersonaConPlaza } from 'components/SeguimientoDeRegistro/ListaDeSeguimientos';
import { onError } from 'utils/handlers';
import { formatReceived } from 'utils/formatValues';
import {
  getAccionesDeSeguimiento,
  patchAccionDeSeguimiento,
  postAccionDeSeguimiento,
} from 'api/configuracion/seguimientoDeRegistro';

const { Text } = Typography;

const rules = {
  required: [{ required: true, message: 'El campo es requerido' }],
};

const formatDate = (value) => (value ? Moment(value).format('DD/MM/YYYY') : '');

// Permisos locales: el grid vive dentro de la nota de seguimiento, que ya validó
// que el usuario puede editarla; aquí solo se habilitan los botones de la Table.
const permission = {
  add: true, change: true, delete: false, hasAllViewPermission: true,
};
const permissionCerrada = {
  add: false, change: false, delete: false, hasAllViewPermission: true,
};

const TEXTO_DE_AVISO = {
  CIERRE: 'Cerró la nota',
  REAPERTURA: 'Reabrió la nota',
};

// Historial completo de una Nota de Seguimiento en forma de tabla: la solicitud del emisor, los
// mensajes de ida y vuelta y los avisos de cierre/reapertura. Se actualiza con `refresco` cuando
// algo cambia en la conversación o en los adjuntos.
function AccionesDeSeguimiento({
  seguimiento, esEmisor, cerrada, refresco, onCambio,
}) {
  const seguimientoId = seguimiento?.id;
  const [form] = Form.useForm();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const fetchData = async () => {
    if (!seguimientoId) {
      setData([]);
      return;
    }
    setLoading(true);
    const acciones = await getAccionesDeSeguimiento(seguimientoId);
    setData(acciones.map((e) => ({
      ...e,
      key: e.id,
      acciones_realizadas: TEXTO_DE_AVISO[e.tipo] || e.acciones_realizadas,
    })));
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [seguimientoId, refresco]);

  useEffect(() => {
    setSelectedRowKeys([]);
  }, [seguimientoId]);

  // Primer renglón: la solicitud con la que el emisor abrió la nota.
  const filas = seguimiento ? [
    {
      key: 'nota',
      esNota: true,
      rol: 'EMISOR',
      usuario_empleado: seguimiento.emisor_empleado,
      usuario_nombre: seguimiento.emisor_nombre,
      usuario_puesto: seguimiento.emisor_puesto,
      usuario_area: seguimiento.emisor_area,
      fecha_de_registro: seguimiento.fecha_de_registro,
      se_requiere: seguimiento.se_requiere,
      fecha_compromiso: seguimiento.fecha_compromiso,
      observaciones: seguimiento.observaciones,
    },
    ...data,
  ] : [];

  const onCancel = () => {
    setVisible(false);
    form.resetFields();
  };

  const onClickAdd = () => {
    form.resetFields();
    form.setFieldsValue({ fecha_de_registro: Moment() });
    setSelectedRowKeys([]);
    setVisible(true);
  };

  const onClickEdit = () => {
    const [key] = selectedRowKeys;
    const registro = data.find((e) => e.id === key);
    if (!registro) {
      message.info('La solicitud inicial se edita desde la pestaña General.');
      return;
    }
    if (registro.tipo !== 'MENSAJE') {
      message.info('Los avisos de cierre y reapertura no se pueden modificar.');
      return;
    }
    form.setFieldsValue(formatReceived(registro));
    setVisible(true);
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      if (!esEmisor) delete values.comentario_de_cierre;
      const [key] = selectedRowKeys;
      const saved = await (key
        ? patchAccionDeSeguimiento(key, values)
        : postAccionDeSeguimiento({ ...values, seguimiento: seguimientoId }));
      if (saved) {
        onCancel();
        await fetchData();
        onCambio?.();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const columns = [
    {
      titleText: 'Fecha',
      dataIndex: 'fecha_de_captura',
      width: 110,
      render: (value, record) => formatDate(value || record.fecha_de_registro),
    },
    {
      titleText: 'Usuario',
      dataIndex: 'usuario_empleado',
      width: 260,
      render: (value, record) => (
        <PersonaConPlaza
          nombre={value || record.usuario_nombre}
          plaza={record.usuario_puesto}
          area={record.usuario_area}
        />
      ),
    },
    {
      titleText: 'Rol',
      dataIndex: 'rol',
      width: 100,
      render: (value) => (
        <Tag color={value === 'EMISOR' ? 'blue' : 'green'}>{value}</Tag>
      ),
    },
    { titleText: 'Se Requiere', dataIndex: 'se_requiere', width: 200 },
    {
      titleText: 'Fecha de Registro',
      dataIndex: 'fecha_de_registro',
      width: 130,
      render: formatDate,
    },
    {
      titleText: 'Fecha Compromiso',
      dataIndex: 'fecha_compromiso',
      width: 130,
      render: formatDate,
    },
    {
      titleText: 'Fecha de Atención',
      dataIndex: 'fecha_de_atencion',
      width: 130,
      render: formatDate,
    },
    { titleText: 'Acción(es) Realizada(s)', dataIndex: 'acciones_realizadas', width: 220 },
    { titleText: 'Observaciones', dataIndex: 'observaciones', width: 200 },
    { titleText: 'Comentario de Cierre', dataIndex: 'comentario_de_cierre', width: 200 },
  ];

  if (!seguimientoId) {
    return (
      <Text type="secondary">
        Guarde la nota de seguimiento para registrar acciones.
      </Text>
    );
  }

  const controls = { onClickAdd, onClickEdit };

  return (
    <Spin tip="Cargando..." spinning={loading}>
      <Table
        cols={columns}
        data={filas}
        rowSelection={{ selectedRowKeys, type: 'radio' }}
        handleOnRowClick={(record) => setSelectedRowKeys([record.key])}
        controls={controls}
        permission={cerrada ? permissionCerrada : permission}
        allowDelete={false}
        mobileColIndex={3}
      />
      <Modal
        open={visible}
        title={(
          <FormSubmitControls
            label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Acción`}
            onFinish={onFinish}
            onCancel={onCancel}
            mutable
            loading={loading}
          />
        )}
        closable={false}
        maskClosable={!loading}
        footer={null}
        width={600}
        destroyOnClose
        forceRender
      >
        <Form layout="vertical" form={form} onFinish={onFinish} scrollToFirstError>
          <Row gutter={10}>
            <Col span={24}>
              <Form.Item name="se_requiere" label="Se Requiere">
                <Input.TextArea allowClear autoSize={{ minRows: 1, maxRows: 4 }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="fecha_de_registro" label="Fecha de Registro" rules={rules.required}>
                <DatePicker format="DD/MM/YYYY" placeholder="" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="fecha_compromiso" label="Fecha Compromiso">
                <DatePicker format="DD/MM/YYYY" placeholder="" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="fecha_de_atencion" label="Fecha de Atención">
                <DatePicker format="DD/MM/YYYY" placeholder="" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="acciones_realizadas" label="Acción(es) Realizada(s)">
                <Input.TextArea allowClear autoSize={{ minRows: 1, maxRows: 4 }} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="observaciones" label="Observaciones">
                <Input.TextArea allowClear autoSize={{ minRows: 1, maxRows: 4 }} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="comentario_de_cierre"
                label="Comentario de Cierre"
              >
                <Input.TextArea
                  allowClear
                  autoSize={{ minRows: 1, maxRows: 4 }}
                  disabled={!esEmisor}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </Spin>
  );
}

AccionesDeSeguimiento.propTypes = {
  seguimiento: PropTypes.shape({
    id: PropTypes.number,
    emisor_empleado: PropTypes.string,
    emisor_nombre: PropTypes.string,
    emisor_puesto: PropTypes.string,
    emisor_area: PropTypes.string,
    fecha_de_registro: PropTypes.string,
    se_requiere: PropTypes.string,
    fecha_compromiso: PropTypes.string,
    observaciones: PropTypes.string,
  }),
  esEmisor: PropTypes.bool,
  cerrada: PropTypes.bool,
  refresco: PropTypes.number,
  onCambio: PropTypes.func,
};

AccionesDeSeguimiento.defaultProps = {
  seguimiento: null,
  esEmisor: false,
  cerrada: false,
  refresco: 0,
  onCambio: null,
};

export default AccionesDeSeguimiento;
